import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    EKycFlowType,
    FormHeader,
    IFormNavigationControl,
    IKycFlowDto,
    MixedMerchantDetails,
    PageContainer,
    PageWrapper,
    SpinnerContainer,
    StandardBox,
    TOrganizationType,
    canEditKycFlow,
    fetchKycDetailsThunk,
    getBestKycFlow,
    getCompanyInfo,
    getQueryValueFromCurrentUrl,
    initiateKycFlowThunk,
    kycSelectors,
    merchantSelectors,
    useEffectAsync,
    useSelector,
} from "swiipe.portal.shared"
import DigitealKycForm from "../../components/kyc/DigitealKycForm"
import KycOnboardingMessage from "../../components/kyc/KycOnboardingMessage"
import { navigationService } from "../../services/navigationService"
import { StoreState } from "../../store/StoreState"
import { digitealOnboardingConnectThunk } from "../../store/thunks/digitealThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./DigitealKycPage.scss"

interface IDigitealKycPageProps {
    withWebshopIds: string[]
    onGoBack: () => void
    onOnboarded: () => void
    viewMode?: boolean
}
interface IDigitealKycPageInnerProps extends IDigitealKycPageProps {
    merchantDetails: MixedMerchantDetails
    flows: IKycFlowDto[]
}

const DigitealKycPage = (props: IDigitealKycPageProps) => {
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const flows = useSelector((state: StoreState) => kycSelectors.flows(state, merchantDetails?.swMerchant.swMerchantId ?? ""))

    if (!merchantDetails || !flows) {
        return null
    }

    return <DigitealKycPageInner merchantDetails={merchantDetails} flows={flows} {...props} />
}

export type TKycDialogType = "form" | "needsTransfer" | "alreadyOnboarded" | "submitted" | "view"

const DigitealKycPageInner = ({
    merchantDetails,
    withWebshopIds,
    onGoBack,
    onOnboarded,
    viewMode,
    flows,
}: IDigitealKycPageInnerProps) => {
    const swMerchantId = merchantDetails.swMerchant.swMerchantId
    const webshopIdFromQuery = getQueryValueFromCurrentUrl("webshopId")
    const webshopIds = viewMode && webshopIdFromQuery ? [webshopIdFromQuery] : withWebshopIds

    const { t } = useTranslation()
    const dispatch = useReduxDispatch()

    const navigationControl = useMemo<IFormNavigationControl>(() => ({}), [])
    const webshopId = webshopIds[0]
    const flowType: EKycFlowType = "DigitealApi"

    const orgDetails = useSelector((state: StoreState) => kycSelectors.orgDetails(state, swMerchantId))
    const webshopDetails = useSelector((state: StoreState) => kycSelectors.webshopDetails(state, webshopId))

    const flow = getBestKycFlow(flows ?? [], swMerchantId, [webshopId], flowType)
    const canEdit = canEditKycFlow(flow)
    const [activeDialog, setActiveDialog] = useState<TKycDialogType>(!flow || canEditKycFlow(flow) ? "form" : "view")

    const isSettingUpFlow = useMemo(() => !flow, [merchantDetails])
    const isEditingFlow = useMemo(() => !flow || canEditKycFlow(flow), [merchantDetails])

    useEffectAsync(async () => {
        const onboardCreditCards = () => {
            dispatch(
                digitealOnboardingConnectThunk({
                    swMerchantId: merchantDetails.swMerchant.swMerchantId,
                    webshopIds: webshopIds,
                    paymentType: "CreditCard",
                })
            )
        }

        if (flow?.state === "Complete" && flow.subState === "None") {
            onboardCreditCards()
            setActiveDialog("alreadyOnboarded")
        }
        if (flow?.state === "Initiated" && flow.subState === "AwaitingCompanyTransferForComplete") {
            onboardCreditCards()
            setActiveDialog("needsTransfer")
        }
        if (flow?.subState === "AwaitingConfirmationForComplete") {
            onboardCreditCards()
            setActiveDialog("submitted")
        }
        if (!orgDetails) {
            dispatch(fetchKycDetailsThunk(swMerchantId, TOrganizationType.SwMerchant, webshopId, false))
        }
        if (flows && !flow) {
            dispatch(initiateKycFlowThunk(swMerchantId, TOrganizationType.SwMerchant, webshopId, flowType))
        }
    }, [flow])

    useEffect(() => {
        dispatch(getCompanyInfo(merchantDetails.swMerchant.countryCode, merchantDetails.swMerchant.registrationNumber))
    }, [merchantDetails])

    const showSpinner = !flow || !orgDetails
    return (
        <PageWrapper widest>
            <PageContainer transparent classNameBody="organization-kyc-page">
                <FormHeader onClose={() => navigationService.navigate("/plugins")} title={"Onboard for Digiteal".toUpperCase()} />
                {showSpinner && (
                    <StandardBox className="organization-kyc-page__spinner-container" padding="30-20" borders="rounded">
                        <SpinnerContainer
                            minHeight={100}
                            showSpinner={showSpinner}
                            className="organization-kyc-form__spinner"
                        ></SpinnerContainer>
                    </StandardBox>
                )}
                {activeDialog === "form" && flow && orgDetails && (
                    <DigitealKycForm
                        flow={flow}
                        orgDetails={orgDetails}
                        flowType="DigitealApi"
                        webshopId={webshopIds[0]}
                        merchantDetails={merchantDetails}
                        onGoBack={onGoBack}
                        navigationControl={navigationControl}
                        formStateUpdatedCallback={(newState) => {
                            if (newState === "finished") {
                                onOnboarded()
                            }
                        }}
                        viewMode={!canEdit}
                    />
                )}
                {activeDialog === "needsTransfer" && (
                    <KycOnboardingMessage
                        mainText={
                            <>
                                <p>
                                    <b>{t("digitealKyc.dialogs.title")}</b>
                                </p>
                                <p>{t("digitealKyc.dialogs.needsTransfer.text")}</p>
                            </>
                        }
                        mainButton={{
                            text: t("common.close"),
                            onClick: onOnboarded,
                        }}
                    />
                )}
                {activeDialog === "alreadyOnboarded" && (
                    <KycOnboardingMessage
                        mainText={
                            <>
                                <p>
                                    <b>{t("digitealKyc.dialogs.title")}</b>
                                </p>
                                <p>{t("digitealKyc.dialogs.alreadyOnboarded.text")}</p>
                            </>
                        }
                        mainButton={{
                            text: t("common.close"),
                            onClick: onOnboarded,
                        }}
                    />
                )}
                {activeDialog === "submitted" && (
                    <KycOnboardingMessage
                        mainText={
                            <>
                                <p>
                                    <b>{t("digitealKyc.dialogs.title")}</b>
                                </p>
                                <p>{t("digitealKyc.dialogs.submitted.text")}</p>
                            </>
                        }
                        mainButton={{
                            text: t("common.close"),
                            onClick: onOnboarded,
                        }}
                    />
                )}
            </PageContainer>
        </PageWrapper>
    )
}

export default DigitealKycPage
